import styled from "@emotion/styled";
import { createRoute, Navigate, Outlet } from "@tanstack/react-router";
import { FrameworkDetailsPage } from "pages/app/FrameworkDetailsPage";
import { FrameworkListPage } from "pages/app/FrameworkListPage";

import { FilterableFrameworkBuyersTable } from "components/buyers/FrameworkBuyersTable";
import FrameworkOverview from "components/frameworks/FrameworkOverview";
import { FrameworkSuppliersTable } from "components/frameworks/FrameworkSuppliersTable";
import { DEFAULT_CALL_OFF_NOTICE_COLUMNS } from "components/frameworks/utils";
import { FilterableNoticeTable } from "components/notices/FilterableNoticeTable";
import { EMPTY_FILTERS } from "components/notices/utils";
import { FrameworkFilterOptions } from "components/record_search/types";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useDescribeFramework } from "lib/hooks/api/frameworks/useDescribeFramework";
import { useFrameworkHighlighting } from "lib/hooks/api/frameworks/useFrameworkHighlighting";
import { rootRoute } from "../rootRoute";

const TableContainer = styled.div({
  padding: "24px 0",
  maxWidth: 1280,
  flex: "auto",
});

// ---------------------------------------------------------------------------
// /frameworks layout — groups the framework list and detail pages
// ---------------------------------------------------------------------------

export const frameworksLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/frameworks",
  staticData: { pageName: "", breadcrumbLabel: "Frameworks" },
  component: Outlet,
});

// /frameworks
export const frameworksIndexRoute = createRoute({
  getParentRoute: () => frameworksLayoutRoute,
  path: "/",
  staticData: { pageName: "Framework search" },
  component: FrameworkListPage,
});

// ---------------------------------------------------------------------------
// /frameworks/$framework_id — shared layout that renders the framework
// header, tab nav, and an Outlet for whichever tab route is active.
//
// Each tab is its own route below. The tabs share the framework data via
// the `useDescribeFramework` hook — react-query dedupes the network call
// so each tab effectively gets the same in-memory record.
//
// `pageName` is intentionally empty: the layout fires its own page-view
// event with framework_id attached (see FrameworkDetailsPage).
// ---------------------------------------------------------------------------

export const frameworkIdLayoutRoute = createRoute({
  getParentRoute: () => frameworksLayoutRoute,
  path: "/$framework_id",
  staticData: { pageName: "" },
  component: FrameworkDetailsPage,
});

// /frameworks/$framework_id — Overview (index)
export const frameworkOverviewRoute = createRoute({
  getParentRoute: () => frameworkIdLayoutRoute,
  path: "/",
  staticData: { pageName: "" },
  component: function FrameworkOverviewTab() {
    const { framework_id } = frameworkIdLayoutRoute.useParams();
    const { data: framework, isLoading } = useDescribeFramework(framework_id);
    const { highlightTerms } = useFrameworkHighlighting(framework_id);
    return (
      <FrameworkOverview framework={framework} highlights={highlightTerms} isLoading={isLoading} />
    );
  },
});

// /frameworks/$framework_id/buyers
export const frameworkBuyersRoute = createRoute({
  getParentRoute: () => frameworkIdLayoutRoute,
  path: "/buyers",
  staticData: { pageName: "" },
  component: function FrameworkBuyersTab() {
    const { framework_id } = frameworkIdLayoutRoute.useParams();
    const { data: framework, isLoading } = useDescribeFramework(framework_id);
    if (isLoading) return <CentredSpinner />;
    // Direct link to /buyers when the framework has no buyers — the tab is
    // disabled in the layout's nav so it can't be reached by clicking; bounce
    // back to Overview rather than showing an indefinite spinner.
    if (!framework?.buyers?.length) {
      return <Navigate to="/frameworks/$framework_id" params={{ framework_id }} replace />;
    }
    return (
      <TableContainer>
        <FilterableFrameworkBuyersTable
          hiddenSections={["suppliers", "signals"]}
          frameworkId={framework.id}
        />
      </TableContainer>
    );
  },
});

// /frameworks/$framework_id/suppliers
export const frameworkSuppliersRoute = createRoute({
  getParentRoute: () => frameworkIdLayoutRoute,
  path: "/suppliers",
  staticData: { pageName: "" },
  component: function FrameworkSuppliersTab() {
    const { framework_id } = frameworkIdLayoutRoute.useParams();
    const { data: framework, isLoading } = useDescribeFramework(framework_id);
    if (isLoading || !framework) return <CentredSpinner />;
    return (
      <TableContainer>
        <FrameworkSuppliersTable
          frameworkData={framework}
          hiddenFilterSections={["buyers", "supplierGuids"]}
        />
      </TableContainer>
    );
  },
});

// /frameworks/$framework_id/call-offs
export const frameworkCallOffsRoute = createRoute({
  getParentRoute: () => frameworkIdLayoutRoute,
  path: "/call-offs",
  staticData: { pageName: "" },
  component: function FrameworkCallOffsTab() {
    const { framework_id } = frameworkIdLayoutRoute.useParams();
    const { data: framework, isLoading } = useDescribeFramework(framework_id);
    return (
      <TableContainer>
        {isLoading || !framework ? (
          <CentredSpinner />
        ) : (
          <FilterableNoticeTable
            defaultFilters={{
              ...EMPTY_FILTERS,
              frameworkIds: [framework.id],
              frameworkFilterOptions: FrameworkFilterOptions.SHOW_ONLY_FRAMEWORK_CALL_OFFS,
            }}
            defaultColumns={DEFAULT_CALL_OFF_NOTICE_COLUMNS}
            hiddenSections={["frameworks"]}
            location="call-offs"
          />
        )}
      </TableContainer>
    );
  },
});

// Catch-all so unknown sub-paths (typos, stale links) fall back to Overview.
export const frameworkDetailCatchallRoute = createRoute({
  getParentRoute: () => frameworkIdLayoutRoute,
  path: "/$",
  staticData: { pageName: "" },
  component: function FrameworkDetailCatchall() {
    const { framework_id } = frameworkIdLayoutRoute.useParams();
    return <Navigate to="/frameworks/$framework_id" params={{ framework_id }} replace />;
  },
});

// ---------------------------------------------------------------------------
// Route tree export
// ---------------------------------------------------------------------------

export const frameworksRouteTree = frameworksLayoutRoute.addChildren([
  frameworksIndexRoute,
  frameworkIdLayoutRoute.addChildren([
    frameworkOverviewRoute,
    frameworkBuyersRoute,
    frameworkSuppliersRoute,
    frameworkCallOffsRoute,
    frameworkDetailCatchallRoute,
  ]),
]);
